/**
 * Pathology BOM / consumable stock checks for sample collection and result gating.
 * Mirrors medicineIssueStockCheck patterns (statuses, blocking, available qty).
 * Does not mutate inventory — callers deduct via deductInventory after a pass.
 */

import { isExpiredExpiryDate } from "./pathExpiryThreshold";
import {
  buildLabTestLookupMaps,
  parseLabTestBomEntries,
  resolveLabTestForUsage,
} from "./pathInventorySummary";

export const PATH_BOM_STOCK_STATUS = {
  OK: "OK",
  NOT_IN_INVENTORY: "NOT_IN_INVENTORY",
  OUT_OF_STOCK: "OUT_OF_STOCK",
  INSUFFICIENT_STOCK: "INSUFFICIENT_STOCK",
  INVALID_QTY: "INVALID_QTY",
  NO_BOM: "NO_BOM",
};

const normalizeNameKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const getBomId = (item) => {
  const raw = item?.bomId ?? item?.bom_id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const getExpiryValue = (item) =>
  item?.expirationDate ||
  item?.expiryDate ||
  item?.expiry_date ||
  item?.expiry ||
  item?.expirydate ||
  null;

const getBatchNumber = (item) =>
  item?.batchNumber || item?.batch_number || item?.batch || null;

const getQuantity = (item) => {
  const n = Number(item?.quantity);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Whether a path inventory batch can be consumed (qty > 0, not expired).
 */
export const isUsablePathInventoryBatch = (
  item,
  { allowMissingExpiry = true } = {}
) => {
  if (!item || !(getQuantity(item) > 0)) return false;
  const expiryDateValue = getExpiryValue(item);
  if (!expiryDateValue) return allowMissingExpiry;
  return !isExpiredExpiryDate(expiryDateValue);
};

/**
 * Resolve inventory rows for a BOM by bomId and/or item name.
 */
export const resolvePathBomInventoryMatch = (
  inventoryList,
  { bomId, itemName } = {},
  { allowMissingExpiry = true } = {}
) => {
  const list = Array.isArray(inventoryList) ? inventoryList : [];
  const idNum = bomId != null && bomId !== "" ? Number(bomId) : NaN;
  const nameKey = normalizeNameKey(itemName);

  const byId = Number.isFinite(idNum)
    ? list.filter((item) => Number(getBomId(item)) === idNum)
    : [];
  const byName = nameKey
    ? list.filter(
        (item) =>
          normalizeNameKey(item?.name || item?.bomName || item?.itemName) ===
          nameKey
      )
    : [];

  const merged = new Map();
  [...byId, ...byName].forEach((item) => {
    const key = item?.id != null ? `id-${item.id}` : `row-${merged.size}`;
    if (!merged.has(key)) merged.set(key, item);
  });
  const items = [...merged.values()];

  if (items.length === 0) {
    return {
      matchType: "none",
      resolvedBomId: Number.isFinite(idNum) ? idNum : null,
      items: [],
      usableBatches: [],
    };
  }

  const usableBatches = items
    .filter((item) => isUsablePathInventoryBatch(item, { allowMissingExpiry }))
    .map((item) => ({
      inventoryId: item.id ?? null,
      bomId: getBomId(item),
      itemName: item.name || item.bomName || item.itemName || itemName || "",
      batchNumber: getBatchNumber(item) || "N/A",
      expiryDate: getExpiryValue(item),
      quantity: getQuantity(item),
      unitType: item.unitType || item.unit_type || null,
    }))
    .sort((a, b) => {
      const dateA = a.expiryDate ? new Date(a.expiryDate).getTime() : Infinity;
      const dateB = b.expiryDate ? new Date(b.expiryDate).getTime() : Infinity;
      return dateA - dateB;
    });

  const preferred = usableBatches[0] || items[0];
  const resolvedFromPreferred = getBomId(preferred) ?? Number(getBomId(items[0]));
  const resolvedBomId = Number.isFinite(resolvedFromPreferred)
    ? resolvedFromPreferred
    : Number.isFinite(idNum)
      ? idNum
      : null;

  let matchType = "none";
  if (byId.length > 0 && byName.length > 0) matchType = "both";
  else if (byId.length > 0) matchType = "id";
  else if (byName.length > 0) matchType = "name";

  return { matchType, resolvedBomId, items, usableBatches };
};

export const getAvailableStockForBom = (
  inventoryList,
  bomId,
  { allowMissingExpiry = true, itemName } = {}
) => {
  const { usableBatches } = resolvePathBomInventoryMatch(
    inventoryList,
    { bomId, itemName },
    { allowMissingExpiry }
  );
  return usableBatches.reduce((sum, batch) => sum + (Number(batch.quantity) || 0), 0);
};

/**
 * Evaluate one BOM / consumable line.
 */
export const evaluatePathBomStock = ({
  bomId,
  itemName,
  requiredQty,
  inventoryList,
  allowMissingExpiry = true,
  labTestNames = [],
}) => {
  const name = itemName || "Unknown consumable";
  const needed = Number(requiredQty) || 0;
  const testsLabel =
    Array.isArray(labTestNames) && labTestNames.length
      ? ` (for ${labTestNames.join(", ")})`
      : "";

  if (needed <= 0) {
    return {
      status: PATH_BOM_STOCK_STATUS.INVALID_QTY,
      bomId,
      resolvedBomId: bomId ?? null,
      itemName: name,
      requiredQty: needed,
      availableQty: 0,
      matchType: "none",
      blocking: true,
      usableBatches: [],
      labTestNames,
      message: `${name}${testsLabel}: required quantity must be greater than 0.`,
    };
  }

  const match = resolvePathBomInventoryMatch(
    inventoryList,
    { bomId, itemName: name },
    { allowMissingExpiry }
  );
  const availableQty = match.usableBatches.reduce(
    (sum, batch) => sum + (Number(batch.quantity) || 0),
    0
  );

  if (match.matchType === "none") {
    return {
      status: PATH_BOM_STOCK_STATUS.NOT_IN_INVENTORY,
      bomId,
      resolvedBomId: bomId ?? null,
      itemName: name,
      requiredQty: needed,
      availableQty: 0,
      matchType: "none",
      blocking: true,
      usableBatches: [],
      labTestNames,
      message: `${name}${testsLabel}: not found in pathology inventory. Add stock for this consumable before continuing.`,
    };
  }

  const resolvedId = match.resolvedBomId ?? bomId;

  if (availableQty <= 0) {
    return {
      status: PATH_BOM_STOCK_STATUS.OUT_OF_STOCK,
      bomId,
      resolvedBomId: resolvedId,
      itemName: name,
      requiredQty: needed,
      availableQty: 0,
      matchType: match.matchType,
      blocking: true,
      usableBatches: match.usableBatches,
      labTestNames,
      message: `${name}${testsLabel}: out of stock (no usable quantity / all batches expired). Required ${needed}.`,
    };
  }

  if (needed > availableQty) {
    return {
      status: PATH_BOM_STOCK_STATUS.INSUFFICIENT_STOCK,
      bomId,
      resolvedBomId: resolvedId,
      itemName: name,
      requiredQty: needed,
      availableQty,
      matchType: match.matchType,
      blocking: true,
      usableBatches: match.usableBatches,
      labTestNames,
      message: `${name}${testsLabel}: insufficient stock. Required ${needed}, available ${availableQty}.`,
    };
  }

  return {
    status: PATH_BOM_STOCK_STATUS.OK,
    bomId,
    resolvedBomId: resolvedId,
    itemName: name,
    requiredQty: needed,
    availableQty,
    matchType: match.matchType,
    blocking: false,
    usableBatches: match.usableBatches,
    labTestNames,
    message: `${name}${testsLabel}: OK (${availableQty} available, need ${needed}).`,
  };
};

export const hasBlockingPathBomStockIssues = (checks = []) =>
  checks.some((c) => c?.blocking);

export const pathBomStockStatusLabel = (status) => {
  switch (status) {
    case PATH_BOM_STOCK_STATUS.OK:
      return "In stock";
    case PATH_BOM_STOCK_STATUS.NOT_IN_INVENTORY:
      return "Not in inventory";
    case PATH_BOM_STOCK_STATUS.OUT_OF_STOCK:
      return "Out of stock";
    case PATH_BOM_STOCK_STATUS.INSUFFICIENT_STOCK:
      return "Insufficient stock";
    case PATH_BOM_STOCK_STATUS.INVALID_QTY:
      return "Invalid quantity";
    case PATH_BOM_STOCK_STATUS.NO_BOM:
      return "No consumables";
    default:
      return "Unknown";
  }
};

/**
 * Aggregate BOM requirements across one or more patient lab tests.
 * Same consumable used by multiple tests is summed.
 *
 * @returns {{ requirements: Array, catalogLabTests: Array, labTestIdsToDeduct: number[] }}
 */
export const buildAggregatedBomRequirementsForTests = ({
  patientLabTests = [],
  catalogLabTests = [],
  boms = [],
}) => {
  const bomsById = new Map(
    (Array.isArray(boms) ? boms : []).map((bom) => [Number(bom.id), bom])
  );
  const { byId, byName } = buildLabTestLookupMaps(catalogLabTests);
  const requirementMap = new Map();
  const labTestIdsToDeduct = [];
  const resolvedCatalog = [];

  (Array.isArray(patientLabTests) ? patientLabTests : []).forEach((patientTest) => {
    const testName =
      patientTest?.resolvedTestName ||
      patientTest?.testName ||
      patientTest?.name ||
      "";
    const catalog = resolveLabTestForUsage({
      labTestId:
        patientTest?.labTestId ??
        patientTest?.testId ??
        patientTest?.lab_test_id ??
        patientTest?.test_id,
      testName,
      labTestsById: byId,
      labTestsByName: byName,
    });
    if (!catalog?.id) return;

    const catalogId = Number(catalog.id);
    if (!labTestIdsToDeduct.includes(catalogId)) {
      labTestIdsToDeduct.push(catalogId);
      resolvedCatalog.push(catalog);
    }

    const entries = parseLabTestBomEntries(catalog, bomsById);
    entries.forEach((entry) => {
      const key = entry.bomId
        ? `bom-${entry.bomId}`
        : `name-${normalizeNameKey(entry.itemName)}`;
      if (!requirementMap.has(key)) {
        requirementMap.set(key, {
          bomId: entry.bomId,
          itemName: entry.itemName,
          requiredQty: 0,
          labTestNames: [],
          labTestIds: [],
        });
      }
      const row = requirementMap.get(key);
      row.requiredQty += Number(entry.requiredQuantity) || 0;
      if (testName && !row.labTestNames.includes(testName)) {
        row.labTestNames.push(testName);
      }
      if (!row.labTestIds.includes(catalogId)) {
        row.labTestIds.push(catalogId);
      }
      if (!row.itemName && entry.itemName) row.itemName = entry.itemName;
    });
  });

  return {
    requirements: Array.from(requirementMap.values()),
    catalogLabTests: resolvedCatalog,
    labTestIdsToDeduct,
  };
};

/**
 * Evaluate aggregated BOM stock for the given patient lab tests.
 */
export const evaluatePathBomStockForLabTests = ({
  patientLabTests = [],
  catalogLabTests = [],
  boms = [],
  inventoryList = [],
  allowMissingExpiry = true,
}) => {
  const { requirements, catalogLabTests: resolved, labTestIdsToDeduct } =
    buildAggregatedBomRequirementsForTests({
      patientLabTests,
      catalogLabTests,
      boms,
    });

  if (!requirements.length) {
    return {
      checks: [],
      hasBlocking: false,
      labTestIdsToDeduct,
      catalogLabTests: resolved,
      hasBomRequirements: false,
    };
  }

  const checks = requirements.map((req) =>
    evaluatePathBomStock({
      bomId: req.bomId,
      itemName: req.itemName,
      requiredQty: req.requiredQty,
      inventoryList,
      allowMissingExpiry,
      labTestNames: req.labTestNames,
    })
  );

  return {
    checks,
    hasBlocking: hasBlockingPathBomStockIssues(checks),
    labTestIdsToDeduct,
    catalogLabTests: resolved,
    hasBomRequirements: true,
  };
};
